import { APP_VERSION, PASSWORD_MIN_LENGTH } from '@iculogbook/shared';
import { useState } from 'react';
import { useNavigate } from 'react-router';

import { useAuth } from '../../auth/AuthProvider.tsx';
import {
  Button,
  Card,
  Dialog,
  Field,
  PasswordInput,
  TextInput,
  useToast,
} from '../../components/index.ts';
import { exportJson } from '../data/backup.ts';

/**
 * Settings → account: the name on your PDFs, your password, and the way out.
 *
 * Account deletion is the one irreversible action in the application, so it
 * behaves like one: it asks for the password, states plainly what happens and
 * when, and offers an export first with a button rather than a suggestion.
 * COMPLIANCE.md §6.
 */
export function AccountSection() {
  const { user, signOut } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [displayName, setDisplayName] = useState(user?.displayName ?? '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [deletePassword, setDeletePassword] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  async function saveName(): Promise<void> {
    setBusy(true);
    try {
      const response = await fetch('/api/account', {
        method: 'PATCH',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ displayName: displayName.trim() }),
      });

      showToast(
        response.ok
          ? { message: 'Name updated. It will appear on your next PDF.', tone: 'success' }
          : { message: 'That name could not be saved.', tone: 'danger' },
      );
    } finally {
      setBusy(false);
    }
  }

  async function changePassword(): Promise<void> {
    setBusy(true);
    try {
      const response = await fetch('/api/account/password', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ currentPassword, newPassword }),
      });

      if (response.ok) {
        // Every session is destroyed, including this one — see the route.
        showToast({ message: 'Password changed. Please sign in again.', tone: 'success' });
        await signOut();
        void navigate('/login', { replace: true });
        return;
      }

      const body = (await response.json().catch(() => null)) as {
        error?: { code?: string };
      } | null;

      showToast({
        message:
          body?.error?.code === 'INVALID_CREDENTIALS'
            ? 'That is not your current password.'
            : 'Choose a longer or less common password.',
        tone: 'danger',
      });
    } finally {
      setBusy(false);
      setCurrentPassword('');
      setNewPassword('');
    }
  }

  async function deleteAccount(): Promise<void> {
    setBusy(true);
    try {
      const response = await fetch('/api/account/delete', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ password: deletePassword }),
      });

      if (!response.ok) {
        showToast({ message: 'That password is not correct.', tone: 'danger' });
        return;
      }

      setConfirming(false);
      // Clears Dexie as well as the server session.
      await signOut();
      void navigate('/login', { replace: true });
    } finally {
      setBusy(false);
      setDeletePassword('');
    }
  }

  return (
    <>
      <Card title="Account">
        <Field label="Name on your PDFs" help="This is the only name ICULogbook shows anywhere.">
          <TextInput
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value)}
            maxLength={100}
          />
        </Field>

        <div className="pt-3">
          <Button
            variant="secondary"
            loading={busy}
            disabled={displayName.trim() === '' || displayName === user?.displayName}
            onClick={() => void saveName()}
          >
            Save name
          </Button>
        </div>

        <div className="mt-5 flex flex-col gap-3 border-t border-line pt-4">
          <h3 className="text-sm font-medium text-text">Change password</h3>

          <Field label="Current password">
            <PasswordInput
              value={currentPassword}
              onChange={(event) => setCurrentPassword(event.target.value)}
              autoComplete="current-password"
            />
          </Field>

          <Field
            label="New password"
            help={`At least ${String(PASSWORD_MIN_LENGTH)} characters. Length matters more than symbols.`}
          >
            <PasswordInput
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
              autoComplete="new-password"
            />
          </Field>

          <p className="text-xs text-faint">
            Changing your password signs out every device, including this one.
          </p>

          <div>
            <Button
              variant="secondary"
              loading={busy}
              disabled={currentPassword === '' || newPassword.length < PASSWORD_MIN_LENGTH}
              onClick={() => void changePassword()}
            >
              Change password
            </Button>
          </div>
        </div>

        <p className="pt-5 text-xs text-faint">
          {user?.email ?? ''} · ICULogbook {APP_VERSION}
        </p>
      </Card>

      <Card title="Delete your account">
        <p className="text-sm text-muted">
          This removes your account and every entry in it. Nothing is kept for us — it is not a way
          of hiding your logbook while we still hold it.
        </p>

        <div className="flex flex-wrap gap-2 pt-3">
          <Button
            variant="secondary"
            loading={busy}
            onClick={() =>
              void exportJson(user?.id ?? '', user?.displayName ?? '').then(() =>
                showToast({ message: 'Export downloaded. Keep it somewhere safe.' }),
              )
            }
          >
            Export everything first
          </Button>
          <Button variant="ghost" className="text-danger" onClick={() => setConfirming(true)}>
            Delete my account
          </Button>
        </div>
      </Card>

      <Dialog
        open={confirming}
        onClose={() => setConfirming(false)}
        title="Delete your account?"
        description={
          <>
            <p>
              Your account is closed immediately and everything in it is permanently deleted after
              30 days. The 30 days exist so that a deletion made by accident can be reversed by
              contacting us — after that it is gone, including from backups within a further 30
              days.
            </p>
            <p className="pt-2">
              If you have not exported your logbook, close this and do that first. There is no way
              to get it back afterwards.
            </p>
          </>
        }
        footer={
          <>
            <Button onClick={() => setConfirming(false)}>Cancel</Button>
            <Button
              variant="danger"
              loading={busy}
              disabled={deletePassword === ''}
              onClick={() => void deleteAccount()}
            >
              Delete permanently
            </Button>
          </>
        }
      >
        <Field label="Confirm your password" className="pt-2">
          <PasswordInput
            value={deletePassword}
            onChange={(event) => setDeletePassword(event.target.value)}
            autoComplete="current-password"
          />
        </Field>
      </Dialog>
    </>
  );
}
